/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Chart } from 'react-google-charts';

import {
  addMonths,
  eachMonthOfInterval,
  endOfMonth,
  format,
  parseISO,
} from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import {
  formatPrice,
  formatNumber2CasasDecimais,
  TIPO_USUARIO,
} from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import { ContainerFlexWrap, RelatorioHeader } from './styles';
import getResponseErrors from '../../../utils/getResponseErrors';

interface IDespesa {
  descricao: string;
  valor: number;
  valorFormatado: string;
  percentualDespesa: number;
  percentualReceita: number;
  percentualDespesaFormatado: string;
  percentualReceitaFormatado: string;
}

interface IRelatorio {
  receitas: number;
  despesas: IDespesa[];
}

interface ITotais {
  totalDespesasFormatado: string;
  totalReceitasFormatado: string;
  totalPercentualDespesaReceita: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IGenericType {
  [key: string]: any;
}

interface IGraficos {
  clinica: string;
  graficoDespesa: IGenericType[];
  dadosDespesa: IGenericType[];
}

export interface IItemResumoMes {
  descricao: string;
  mes: string;
  total: number;
}

interface IRetornoResumoGerencialAcumulado {
  CliCodigo: number;
  CliNome: string;
  valores: IItemResumoMes[];
}

const DespesasResumo: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica, imagensClinica } = useClinica();

  const logo =
    imagensClinica.find(img => img.nome === 'logoRelatorio')?.arquivo_url || '';

  const formRefBusca = useRef<FormHandles>(null);
  const formRefBuscaAcumulado = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio>();
  const [graficoDespesas, setGraficoDespesas] = useState<IGenericType[]>();
  const [graficoDespesasAcumulado, setGraficoDespesasAcumulado] = useState<
    IGraficos[]
  >();
  const [totais, setTotais] = useState<ITotais>();
  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const initialDataAcumulado = {
    dataInicio: format(addMonths(new Date(), -6), 'yyyy-MM'),
    dataFim: format(new Date(), 'yyyy-MM'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [clinica, setClinica] = useState('');

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setClinica(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const response = await api.get<IRelatorio>(
          `/relatorios/despesasresumo?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&CliCodigo=${dados.CliCodigo}`,
        );

        const totalDespesas = response.data.despesas.reduce((tot, item) => {
          return tot + Number(item.valor);
        }, 0);

        const totalReceitas = response.data.receitas;

        const despesas = response.data.despesas.map(rel => {
          const percentualDespesa = (rel.valor / totalDespesas) * 100;
          const percentualReceita = (rel.valor / totalReceitas) * 100;

          return {
            ...rel,
            valorFormatado: formatPrice(rel.valor),
            percentualDespesa,
            percentualReceita,
            percentualDespesaFormatado: `${formatNumber2CasasDecimais(
              percentualDespesa.toString(),
            )}%`,
            percentualReceitaFormatado: `${formatNumber2CasasDecimais(
              percentualReceita.toString(),
            )}%`,
          };
        });

        const graficoLegenda: IGenericType[] = [['Despesa', 'Total']];

        const despesasGrafico: IGenericType[] = response.data.despesas.map(
          rel => {
            return [rel.descricao, rel.valor];
          },
        );

        const percentualGeral = (totalDespesas / totalReceitas) * 100;

        setDadosRelatorio({ despesas, receitas: totalReceitas });
        setTotais({
          totalReceitasFormatado: formatPrice(totalReceitas),
          totalDespesasFormatado: formatPrice(totalDespesas),
          totalPercentualDespesaReceita: `${formatNumber2CasasDecimais(
            percentualGeral.toString(),
          )}%`,
        });

        setGraficoDespesas(graficoLegenda.concat(despesasGrafico));
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica],
  );

  const handleSubmitBuscaAcumulado = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBuscaAcumulado.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = new Date(
          `${dados.dataInicio.toString()}-01 04:00:00`,
        );
        const dataFimSelecionada = endOfMonth(
          new Date(`${dados.dataFim.toString()}-01 04:00:00`),
        );

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        const parametrosOpcionais = dados.CliCodigo
          ? `&CliCodigo=${dados.CliCodigo}`
          : '';

        const response = await api.get<IRetornoResumoGerencialAcumulado[]>(
          `/relatorios/despesasacumulado?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        const meses = eachMonthOfInterval({
          start: dataInicioSelecionada,
          end: addMonths(dataFimSelecionada, -1),
        }).map(data => format(data, 'MM-yyyy'));

        const graficoReceitaDespesaLegenda: IGenericType[] = [
          ['Mes', 'Despesas'],
        ];

        const graficoGeral: IGraficos[] = [];

        response.data.forEach(rel => {
          const receitaDespesa: IGenericType[] = [];

          const dadosDespesa: IGenericType[] = [];

          meses.forEach(mes => {
            const mesFiltrado = rel.valores.filter(val => val.mes === mes);

            const despesas =
              mesFiltrado.find(val => val.descricao === 'despesas')?.total || 0;

            receitaDespesa.push([mes, despesas]);
            dadosDespesa.push([mes.replace('-', '/'), formatPrice(despesas)]);
          });

          graficoGeral.push({
            clinica: rel.CliNome,
            graficoDespesa: graficoReceitaDespesaLegenda.concat(receitaDespesa),
            dadosDespesa: graficoReceitaDespesaLegenda.concat(dadosDespesa),
          });
        });

        //   console.dir(graficoGeral);

        setGraficoDespesasAcumulado(graficoGeral);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBuscaAcumulado.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    formRefBuscaAcumulado.current?.submitForm();
  }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="bg-white border p-2 my-3 d-flex justify-content-between align-items-center">
          <img src={logo} alt="Agenda facil" />
          <h2 id="topoBusca">Demonstrativo de Despesas</h2>

          <span className="pr-2">
            Impresso em
            {dataExibe?.dataHora}{' '}
          </span>
        </div>

        <div className="d-print-none bg-white border p-2 mb-2">
          <RelatorioHeader className="mt-2">
            ACUMULADO MENSAL {`- ${clinica} -`}
          </RelatorioHeader>
          <div className="bg-white border p-2">
            <div className="d-print-none">
              {optionsClinica.length > 0 && user && (
                <Form
                  ref={formRefBuscaAcumulado}
                  initialData={initialDataAcumulado}
                  onSubmit={handleSubmitBuscaAcumulado}
                >
                  <Row>
                    <Col md={2}>
                      <Input
                        name="dataInicio"
                        label="De"
                        type="month"
                        required
                      />
                    </Col>
                    <Col md={2}>
                      <Input name="dataFim" label="Até" type="month" required />
                    </Col>
                    <Col md={2}>
                      <Select
                        name="CliCodigo"
                        label="Clinica"
                        options={optionsClinica}
                        disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                      />
                    </Col>
                    <Col md={2}>
                      <br />
                      <Button
                        type="submit"
                        size="sm"
                        variant="outline-secondary"
                      >
                        BUSCAR
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </div>

            {graficoDespesasAcumulado &&
              graficoDespesasAcumulado.map(cli => (
                <ContainerFlexWrap key={`principal${cli.clinica}`}>
                  <div>
                    <Chart
                      key={`grafAcumulado-${cli.clinica}`}
                      chartType="ColumnChart"
                      data={cli.graficoDespesa}
                      options={{
                        title: `Despesas ${cli.clinica}`,
                        legend: 'none',
                      }}
                      width="100%"
                      height="300px"
                      legendToggle
                    />
                  </div>

                  <div>
                    <h6 className="small my-3">
                      <b>DADOS {cli.clinica}</b>
                    </h6>
                    <table
                      className="table table-hover table-sm table-font-small table-bordered"
                      style={{ borderWidth: '0px' }}
                    >
                      <thead>
                        <tr className="text-center">
                          <th>{cli.dadosDespesa[0][0]}</th>
                          <th>{cli.dadosDespesa[0][1]}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cli.dadosDespesa.map(
                          (dado, index) =>
                            index > 0 && (
                              <tr
                                key={`dados-${cli.clinica}-${dado[0]}`}
                                style={{ fontSize: '.7rem', height: '27px' }}
                              >
                                <td>{dado[0]}</td>
                                <td>{dado[1]}</td>
                              </tr>
                            ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </ContainerFlexWrap>
              ))}
          </div>
        </div>

        <RelatorioHeader>
          DESPESAS POR CATEGORIA {`- ${clinica} -`} {dataExibe?.dataDiaSemana}
        </RelatorioHeader>
        <div className="bg-white border p-2" style={{ minHeight: '300px' }}>
          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
            className="pb-2"
          >
            <Row>
              <Col md={2}>
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2}>
                <Input name="dataFim" label="Até" type="date" required />
              </Col>
              <Col md={2}>
                <Select
                  name="CliCodigo"
                  label="Clinica"
                  options={optionsClinica}
                  disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                />
              </Col>
              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '100%' }}>Categoria</th>
                    <th style={{ minWidth: '150px' }}>Total</th>
                    <th style={{ minWidth: '150px' }}>% Despesa</th>
                    <th style={{ minWidth: '150px' }}>% Receita</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.despesas.map(rel => (
                      <tr
                        key={`despesas-${rel.descricao}`}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.descricao}</td>
                        <td>{rel.valorFormatado}</td>
                        <td>{rel.percentualDespesaFormatado}</td>
                        <td>{rel.percentualReceitaFormatado}</td>
                      </tr>
                    ))}

                  <tr
                    key="fdfdsfsdfdf"
                    style={{
                      fontSize: 'Small',
                      height: '30px',
                      fontWeight: 'bold',
                    }}
                  >
                    <td> </td>
                    <td style={{ textAlign: 'right' }}>
                      Total Despesas: {totais?.totalDespesasFormatado}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      Total Receitas: {totais?.totalReceitasFormatado}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      Percentual Despesa/Receita:{' '}
                      {totais?.totalPercentualDespesaReceita}
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
          {graficoDespesas && (
            <Chart
              chartType="PieChart"
              data={graficoDespesas}
              options={{
                title: `% Despesas`,
              }}
              width="100%"
              height="400px"
              legendToggle
            />
          )}
        </div>
      </>
    </Container>
  );
};

export default DespesasResumo;
